import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { AuthenticationService } from '../services/authentication.service';
import { isSecurePath } from '../crypto/secure-channel.service';

// Module-level state — shared across all interceptor invocations
let isRefreshing = false;
const refreshSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService  = inject(AuthService);
  const authHttp     = inject(AuthenticationService);
  const router       = inject(Router);

  // Never attach auth header or intercept 401 for the refresh endpoint itself
  if (req.url.includes('auth/refresh') || req.url.includes('auth/logout')) {
    return next(req);
  }

  // The family vault runs its own session: an in-memory access token and an httpOnly refresh
  // cookie, handled by vaultAuthInterceptor. Attaching this module's localStorage token to a
  // vault request would send a credential from a different login flow — and would quietly
  // reintroduce the storage dependency the vault exists without.
  if (isSecurePath(req.url)) {
    return next(req);
  }

  const token = authService.getToken();
  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401) return throwError(() => err);

      if (isRefreshing) {
        // Another refresh is in progress — queue this request
        return refreshSubject.pipe(
          filter(t => t !== null),
          take(1),
          switchMap(newToken =>
            next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }))
          )
        );
      }

      isRefreshing = true;
      refreshSubject.next(null);

      return authHttp.refreshToken().pipe(
        switchMap(() => {
          isRefreshing = false;
          const newToken = authService.getToken()!;
          refreshSubject.next(newToken);
          return next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }));
        }),
        catchError(refreshErr => {
          isRefreshing = false;
          authService.logout();
          router.navigate(['/login'], { queryParams: { returnUrl: router.url } });
          return throwError(() => refreshErr);
        })
      );
    })
  );
};
